import {
  faArrowLeft,
  faCamera,
  faCheck,
  faClose,
  faFingerprint,
  faLocationDot,
  faQrcode,
  faRightFromBracket,
  faRightToBracket,
} from "@fortawesome/free-solid-svg-icons"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import InputField from "../../components/InputField/InputField"
import { InputPhotoWithContainer } from "../../components/InputPhoto/InputPhoto"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import type { TimeClockPunchPayload } from "../../Objects/TimeClock/TimeClockPunchPayload"
import type { TimeClockStatus } from "../../Objects/TimeClock/TimeClockStatus"
import type { TimeClockValidationType } from "../../Objects/TimeClock/TimeClockValidationType"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"
import { TimeClockQrCodeScannerPopup } from "./TimeClockQrCodeScannerPopup"

type WizardStep = "intro" | "pin" | "qrcode" | "selfie" | "review"

interface TimeClockPunchWizardPopupProps {
  status: TimeClockStatus
  isSaving: boolean
  onClose: () => void
  onConfirm: (payload: TimeClockPunchPayload) => Promise<boolean>
}

export const TimeClockPunchWizardPopup: React.FC<TimeClockPunchWizardPopupProps> = ({
  status,
  isSaving,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const isIn = status.nextDirection === "In"

  const requires = (validation: TimeClockValidationType) => status.requiredValidations.includes(validation)

  const pinMissing = status.requiredValidations.includes("Pin") && !status.hasPin

  const manualSteps = useMemo<WizardStep[]>(() => {
    const steps: WizardStep[] = []

    if (status.requiredValidations.includes("Pin")) steps.push("pin")
    if (status.requiredValidations.includes("QrCode")) steps.push("qrcode")
    if (status.requiredValidations.includes("Selfie")) steps.push("selfie")

    return steps
  }, [status.requiredValidations])

  const steps = useMemo<WizardStep[]>(() => ["intro", ...manualSteps, "review"], [manualSteps])

  const [stepIndex, setStepIndex] = useState(0)

  const [pin, setPin] = useState("")

  const [qrCodeToken, setQrCodeToken] = useState("")

  const [photo, setPhoto] = useState<File | null>(null)

  const [isScannerOpen, setIsScannerOpen] = useState(false)

  const currentStep = steps[stepIndex]

  const goNext = () => setStepIndex((index) => Math.min(index + 1, steps.length - 1))

  const goBack = () => setStepIndex((index) => Math.max(index - 1, 0))

  const handleConfirm = async () => {
    if (isSaving) return

    await onConfirm({
      pin: manualSteps.includes("pin") ? pin : undefined,
      qrCodeToken: manualSteps.includes("qrcode") ? qrCodeToken : undefined,
      photo: manualSteps.includes("selfie") && photo ? photo : undefined,
    })
  }

  const stepLabels: Record<WizardStep, string> = {
    intro: t("my.wizard.step_intro"),
    pin: t("my.wizard.step_pin"),
    qrcode: t("my.wizard.step_qrcode"),
    selfie: t("my.wizard.step_selfie"),
    review: t("my.wizard.step_review"),
  }

  const validationMeta: Record<TimeClockValidationType, { icon: IconProp; label: string }> = {
    Geolocation: { icon: faLocationDot, label: t("my.requires_geolocation") },
    Pin: { icon: faFingerprint, label: t("my.requires_pin") },
    QrCode: { icon: faQrcode, label: t("my.requires_qr") },
    Selfie: { icon: faCamera, label: t("my.requires_selfie") },
  }

  const nextDisabled =
    (currentStep === "intro" && pinMissing) ||
    (currentStep === "pin" && (pinMissing || !pin.trim())) ||
    (currentStep === "qrcode" && !qrCodeToken.trim()) ||
    (currentStep === "selfie" && !photo)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={isIn ? t("my.punch_in") : t("my.punch_out")}
              icon={isIn ? faRightToBracket : faRightFromBracket}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-punch-wizard"
                    title={t("helpPopup:global.timeClockPunchWizard.title")}
                    description={t("helpPopup:global.timeClockPunchWizard.description")}
                    bullets={t("helpPopup:global.timeClockPunchWizard.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="max-h-[72vh] overflow-y-auto p-2">
              <div className="mb-3 flex flex-wrap justify-center gap-2 text-center text-[11px] font-black text-slate-600">
                {steps.map((step, index) => (
                  <span
                    key={step}
                    className={`rounded-lg border px-2 py-2 ${
                      index <= stepIndex
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}
                  >
                    {stepLabels[step]}
                  </span>
                ))}
              </div>

              <Animate key={currentStep} type={AnimationTypes.slideUpIn} duration="0.3s">
                <section className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                  {currentStep === "intro" && (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={isIn ? faRightToBracket : faRightFromBracket}
                          className="text-emerald-700"
                        />

                        <strong className="text-sm text-slate-900">
                          {isIn ? t("my.punch_in") : t("my.punch_out")}
                        </strong>
                      </div>

                      <p className="text-xs leading-relaxed text-slate-600">{t("my.wizard.intro_description")}</p>

                      <div className="flex flex-col gap-2">
                        {status.requiredValidations.map((validation) => (
                          <div
                            key={validation}
                            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                          >
                            <FontAwesomeIcon icon={validationMeta[validation].icon} className="text-sky-700" />

                            {validationMeta[validation].label}
                          </div>
                        ))}
                      </div>

                      {pinMissing && (
                        <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900">
                          {t("my.wizard.pin_missing_warning")}
                        </p>
                      )}

                      <div className={defaultStyle.buttonsController}>
                        <Button
                          typeButton="button"
                          label={t("my.wizard.continue")}
                          bgColor={THEME.colors.buttonBGSaveColor}
                          iconColor={THEME.colors.iconBGSecondaryColor}
                          disabled={nextDisabled}
                          onClick={goNext}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.continue"
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === "pin" && (
                    <div className="flex flex-col gap-3">
                      {pinMissing ? (
                        <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900">
                          {t("my.wizard.pin_missing_warning")}
                        </p>
                      ) : (
                        <InputField
                          type="password"
                          value={pin}
                          required
                          inputMode="numeric"
                          maxLength={8}
                          icon={faFingerprint}
                          placeholder={t("my.requires_pin")}
                          onChange={(event) => setPin(event.target.value)}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.input.pin"
                        />
                      )}

                      <div className={defaultStyle.buttonsController}>
                        <ButtonIcon
                          sizeNumber={SIZE.buttonCloseIconSize}
                          bgColor={THEME.colors.buttonBGCloseColor}
                          iconColor={THEME.colors.buttonFGCloseColor}
                          icon={faArrowLeft}
                          onClick={goBack}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.backPin"
                        />

                        <Button
                          typeButton="button"
                          label={t("my.wizard.continue")}
                          bgColor={THEME.colors.buttonBGSaveColor}
                          iconColor={THEME.colors.iconBGSecondaryColor}
                          disabled={nextDisabled}
                          onClick={goNext}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.continuePin"
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === "qrcode" && (
                    <div className="flex flex-col gap-3">
                      <InputField
                        type="text"
                        value={qrCodeToken}
                        required
                        icon={faQrcode}
                        placeholder={t("my.requires_qr")}
                        onChange={(event) => setQrCodeToken(event.target.value.toUpperCase())}
                        descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.input.qrCode"
                      />

                      <Button
                        typeButton="button"
                        label={t("my.wizard.scan_button")}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.iconBGSecondaryColor}
                        onClick={() => setIsScannerOpen(true)}
                        descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.openScanner"
                      />

                      <div className={defaultStyle.buttonsController}>
                        <ButtonIcon
                          sizeNumber={SIZE.buttonCloseIconSize}
                          bgColor={THEME.colors.buttonBGCloseColor}
                          iconColor={THEME.colors.buttonFGCloseColor}
                          icon={faArrowLeft}
                          onClick={goBack}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.backQrCode"
                        />

                        <Button
                          typeButton="button"
                          label={t("my.wizard.continue")}
                          bgColor={THEME.colors.buttonBGSaveColor}
                          iconColor={THEME.colors.iconBGSecondaryColor}
                          disabled={nextDisabled}
                          onClick={goNext}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.continueQrCode"
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === "selfie" && (
                    <div className="flex flex-col gap-3">
                      <p className="text-xs font-medium text-gray-700">{t("my.requires_selfie")}</p>

                      <InputPhotoWithContainer
                        iconButton
                        handleFileSelected={setPhoto}
                        imageProfile="company"
                        color={THEME.colors.buttonBGPlusColor}
                      />

                      <div className={defaultStyle.buttonsController}>
                        <ButtonIcon
                          sizeNumber={SIZE.buttonCloseIconSize}
                          bgColor={THEME.colors.buttonBGCloseColor}
                          iconColor={THEME.colors.buttonFGCloseColor}
                          icon={faArrowLeft}
                          onClick={goBack}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.backSelfie"
                        />

                        <Button
                          typeButton="button"
                          label={t("my.wizard.continue")}
                          bgColor={THEME.colors.buttonBGSaveColor}
                          iconColor={THEME.colors.iconBGSecondaryColor}
                          disabled={nextDisabled}
                          onClick={goNext}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.continueSelfie"
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === "review" && (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCheck} className="text-emerald-700" />

                        <strong className="text-sm text-slate-900">{t("my.wizard.review_title")}</strong>
                      </div>

                      <div className="flex flex-col gap-2">
                        {requires("Geolocation") && (
                          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                            <FontAwesomeIcon icon={faLocationDot} className="text-sky-700" />

                            {t("my.wizard.review_geolocation")}
                          </div>
                        )}

                        {manualSteps.includes("pin") && (
                          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                            <FontAwesomeIcon icon={faFingerprint} className="text-sky-700" />

                            {t("my.wizard.review_pin_done")}
                          </div>
                        )}

                        {manualSteps.includes("qrcode") && (
                          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                            <FontAwesomeIcon icon={faQrcode} className="text-sky-700" />

                            {t("my.wizard.review_qrcode_done")}
                          </div>
                        )}

                        {manualSteps.includes("selfie") && (
                          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                            <FontAwesomeIcon icon={faCamera} className="text-sky-700" />

                            {t("my.wizard.review_selfie_done")}
                          </div>
                        )}
                      </div>

                      <div className={defaultStyle.buttonsController}>
                        <ButtonIcon
                          sizeNumber={SIZE.buttonCloseIconSize}
                          bgColor={THEME.colors.buttonBGCloseColor}
                          iconColor={THEME.colors.buttonFGCloseColor}
                          icon={faArrowLeft}
                          onClick={goBack}
                          disabled={isSaving}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.backReview"
                        />

                        <Button
                          typeButton="button"
                          label={t("my.wizard.confirm")}
                          bgColor={isIn ? THEME.colors.buttonBGSaveColor : THEME.colors.buttonBGCloseColor}
                          iconColor={THEME.colors.iconBGSecondaryColor}
                          disabled={isSaving}
                          onClick={() => void handleConfirm()}
                          descriptionKey="interactionDescriptions:pages.timeClock.timeClockPunchWizardPopup.button.confirm"
                        />
                      </div>
                    </div>
                  )}
                </section>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>

      {isScannerOpen && (
        <TimeClockQrCodeScannerPopup
          onClose={() => setIsScannerOpen(false)}
          onScan={(text) => {
            setQrCodeToken(text.toUpperCase())
            setIsScannerOpen(false)
          }}
        />
      )}
    </ModalPortal>
  )
}
